import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useSettings, useUpdateSettings } from '@/hooks/use-settings';
import { settingsSchema, type SettingsFormData, type UpdateSettingsData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';

type RecipientField = 'luxuryIntakeRecipients' | 'projectIntakeRecipients';

export type SettingsSection = 'registrations' | 'website' | 'system';

const settingsSectionCopy = {
  registrations: 'Instellingen voor registratieformulieren en betalingen.',
  website: 'Instellingen voor de publieke FAME-website.',
  system: 'Technische instellingen voor de backoffice.',
} satisfies Record<SettingsSection, string>;

const sectionFields = {
  registrations: [
    'articleNumbers',
    'thankYouUrlNl',
    'thankYouUrlFr',
    'thankYouUrlEn',
    'registrationExportDriveFolderId',
  ],
  website: ['luxuryIntakeRecipients', 'projectIntakeRecipients', 'b2bSalesSheetId', 'luxurySalesSheetId'],
  system: [
    'smtpHost',
    'smtpPort',
    'smtpSecure',
    'smtpUsername',
    'smtpPassword',
    'clearSmtpPassword',
    'smtpFrom',
    'translatorProvider',
    'azureTranslatorKey',
    'clearAzureTranslatorKey',
    'azureTranslatorRegion',
    'azureTranslatorEndpoint',
    'googleTranslatorKey',
    'clearGoogleTranslatorKey',
  ],
} satisfies Record<SettingsSection, readonly (keyof SettingsFormData)[]>;

export function SettingsPage({ section = 'system' }: { section?: SettingsSection }) {
  const { data: settings, isLoading } = useSettings(section);
  const updateSettings = useUpdateSettings(section);
  const [newArticleNumber, setNewArticleNumber] = useState('');
  const [newLuxuryRecipient, setNewLuxuryRecipient] = useState('');
  const [newProjectRecipient, setNewProjectRecipient] = useState('');

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema) as Resolver<SettingsFormData>,
    defaultValues: {
      articleNumbers: [],
      thankYouUrlNl: null,
      thankYouUrlFr: null,
      thankYouUrlEn: null,
      luxuryIntakeRecipients: [],
      projectIntakeRecipients: [],
      b2bSalesSheetId: '',
      luxurySalesSheetId: '',
      registrationExportDriveFolderId: '',
      smtpHost: '',
      smtpPort: 587,
      smtpSecure: false,
      smtpUsername: '',
      smtpPassword: '',
      clearSmtpPassword: false,
      smtpFrom: '',
      translatorProvider: 'azure',
      azureTranslatorKey: '',
      clearAzureTranslatorKey: false,
      azureTranslatorRegion: '',
      azureTranslatorEndpoint: 'https://api.cognitive.microsofttranslator.com',
      googleTranslatorKey: '',
      clearGoogleTranslatorKey: false,
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset({
        articleNumbers: settings.articleNumbers ?? [],
        thankYouUrlNl: settings.thankYouUrlNl ?? null,
        thankYouUrlFr: settings.thankYouUrlFr ?? null,
        thankYouUrlEn: settings.thankYouUrlEn ?? null,
        luxuryIntakeRecipients: settings.luxuryIntakeRecipients ?? [],
        projectIntakeRecipients: settings.projectIntakeRecipients ?? [],
        b2bSalesSheetId: settings.b2bSalesSheetId ?? '',
        luxurySalesSheetId: settings.luxurySalesSheetId ?? '',
        registrationExportDriveFolderId: settings.registrationExportDriveFolderId ?? '',
        smtpHost: settings.smtpHost ?? '',
        smtpPort: settings.smtpPort ?? 587,
        smtpSecure: settings.smtpSecure ?? false,
        smtpUsername: settings.smtpUsername ?? '',
        smtpPassword: '',
        clearSmtpPassword: false,
        smtpFrom: settings.smtpFrom ?? '',
        translatorProvider: settings.translatorProvider ?? 'azure',
        azureTranslatorKey: '',
        clearAzureTranslatorKey: false,
        azureTranslatorRegion: settings.azureTranslatorRegion ?? '',
        azureTranslatorEndpoint: settings.azureTranslatorEndpoint ?? 'https://api.cognitive.microsofttranslator.com',
        googleTranslatorKey: '',
        clearGoogleTranslatorKey: false,
      });
    }
  }, [settings, form]);

  const articleNumbers = form.watch('articleNumbers');
  const luxuryIntakeRecipients = form.watch('luxuryIntakeRecipients');
  const projectIntakeRecipients = form.watch('projectIntakeRecipients');
  const translatorProvider = form.watch('translatorProvider');

  const recipientsWithInput = (field: RecipientField, recipient: string, recipients: string[]) => {
    const email = recipient.trim().toLowerCase();
    if (!email) return recipients;
    if (!z.email().safeParse(email).success) {
      form.setError(field, { type: 'validate', message: 'Vul een geldig e-mailadres in.' });
      return null;
    }
    form.clearErrors(field);
    return recipients.includes(email) ? recipients : [...recipients, email];
  };

  const addRecipient = (field: RecipientField, recipient: string, clear: (value: string) => void) => {
    const recipients = recipientsWithInput(field, recipient, form.getValues(field));
    if (!recipients) return;
    form.setValue(field, recipients, { shouldValidate: true });
    clear('');
  };

  const removeRecipient = (field: RecipientField, index: number) => {
    form.setValue(
      field,
      form.getValues(field).filter((_, itemIndex) => itemIndex !== index),
      { shouldValidate: true }
    );
  };

  const addArticleNumber = () => {
    const trimmed = newArticleNumber.trim();
    if (!trimmed || articleNumbers.includes(trimmed)) return;
    form.setValue('articleNumbers', [...articleNumbers, trimmed]);
    setNewArticleNumber('');
  };

  const removeArticleNumber = (index: number) => {
    form.setValue(
      'articleNumbers',
      articleNumbers.filter((_, i) => i !== index)
    );
  };

  const onSubmit = async (data: SettingsFormData) => {
    let submittedData = data;
    const pendingRecipients: [RecipientField, string, (value: string) => void][] = [
      ['luxuryIntakeRecipients', newLuxuryRecipient, setNewLuxuryRecipient],
      ['projectIntakeRecipients', newProjectRecipient, setNewProjectRecipient],
    ];

    for (const [field, recipient] of pendingRecipients) {
      const recipients = recipientsWithInput(field, recipient, submittedData[field]);
      if (!recipients) return;
      submittedData = { ...submittedData, [field]: recipients };
    }

    const updates = Object.fromEntries(
      sectionFields[section].map(field => [field, submittedData[field]])
    ) as UpdateSettingsData;
    await updateSettings.mutateAsync(updates);
    if (section === 'website') {
      setNewLuxuryRecipient('');
      setNewProjectRecipient('');
    }
  };

  if (isLoading) {
    return <div className="text-muted-foreground p-4">Laden...</div>;
  }

  const sectionDescription = settingsSectionCopy[section];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Instellingen</h1>
        <p className="text-muted-foreground">{sectionDescription}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {section === 'registrations' && (
            <section className="space-y-6">
              {/* Article Numbers */}
              <Card>
                <CardHeader>
                  <CardTitle>Artikelnummers</CardTitle>
                  <CardDescription>
                    Bekende artikelnummers worden als suggesties aangeboden in alle formulieren.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nieuw artikelnummer"
                      value={newArticleNumber}
                      onChange={e => setNewArticleNumber(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addArticleNumber();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addArticleNumber}
                      disabled={!newArticleNumber.trim()}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {articleNumbers.length > 0 ? (
                    <ul className="space-y-1">
                      {articleNumbers.map((n, i) => (
                        <li key={i} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                          <span className="font-mono">{n}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => removeArticleNumber(i)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nog geen artikelnummers toegevoegd.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Registratie-export naar Google Drive</CardTitle>
                  <CardDescription>
                    Optioneel. Nieuwe deelnemersoverzichten worden in deze Google Drive-map aangemaakt. Laat leeg om ze
                    in de Drive van het serviceaccount aan te maken.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="registrationExportDriveFolderId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Google Drive-map-ID</FormLabel>
                        <FormControl>
                          <Input placeholder="Map-ID" {...field} />
                        </FormControl>
                        <FormDescription>
                          De map-ID staat in de Google Drive-URL na <code>/folders/</code>.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Thank-you URLs */}
              <Card>
                <CardHeader>
                  <CardTitle>Dank u pagina URL's</CardTitle>
                  <CardDescription>
                    Na een geslaagde betaling wordt de bezoeker doorgestuurd naar de URL die overeenkomt met hun taal.
                    Valt terug op NL als de taal-specifieke URL ontbreekt.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="thankYouUrlNl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nederlands (NL)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://www.example.be/bedankt"
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value || null)}
                          />
                        </FormControl>
                        <FormDescription>Ook de standaard fallback als er geen taalspecifieke URL is.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="thankYouUrlFr"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Frans (FR)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://www.example.be/merci"
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value || null)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="thankYouUrlEn"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Engels (EN)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://www.example.be/thank-you"
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value || null)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </section>
          )}
          {section === 'website' && (
            <section className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Intake-meldingen</CardTitle>
                  <CardDescription>
                    Ontvangers voor nieuwe aanvragen via de FAME-website. Voeg een e-mailadres toe met +, of klik meteen
                    op ‘Instellingen opslaan’ om het toe te voegen. Zonder ontvangers wordt het overeenkomstige bericht
                    niet verstuurd.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <section className="space-y-3">
                    <div>
                      <h3 className="font-medium">Luxury travel intake</h3>
                      <p className="text-sm text-muted-foreground">
                        Ontvang een melding voor elke nieuwe luxury-aanvraag.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="naam@fame-travel.be"
                        value={newLuxuryRecipient}
                        onChange={event => setNewLuxuryRecipient(event.target.value)}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            addRecipient('luxuryIntakeRecipients', newLuxuryRecipient, setNewLuxuryRecipient);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          addRecipient('luxuryIntakeRecipients', newLuxuryRecipient, setNewLuxuryRecipient)
                        }
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {luxuryIntakeRecipients.map((recipient, index) => (
                      <div
                        key={recipient}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      >
                        <span>{recipient}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => removeRecipient('luxuryIntakeRecipients', index)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    {form.formState.errors.luxuryIntakeRecipients?.message && (
                      <p className="text-sm font-medium text-destructive">
                        {form.formState.errors.luxuryIntakeRecipients.message}
                      </p>
                    )}
                  </section>
                  <section className="space-y-3">
                    <div>
                      <h3 className="font-medium">Incentives, events en meetings</h3>
                      <p className="text-sm text-muted-foreground">
                        Ontvang een melding voor elke nieuwe B2B-aanvraag.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="naam@fame-travel.be"
                        value={newProjectRecipient}
                        onChange={event => setNewProjectRecipient(event.target.value)}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            addRecipient('projectIntakeRecipients', newProjectRecipient, setNewProjectRecipient);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          addRecipient('projectIntakeRecipients', newProjectRecipient, setNewProjectRecipient)
                        }
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {projectIntakeRecipients.map((recipient, index) => (
                      <div
                        key={recipient}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      >
                        <span>{recipient}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => removeRecipient('projectIntakeRecipients', index)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    {form.formState.errors.projectIntakeRecipients?.message && (
                      <p className="text-sm font-medium text-destructive">
                        {form.formState.errors.projectIntakeRecipients.message}
                      </p>
                    )}
                  </section>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Google Sheets</CardTitle>
                  <CardDescription>
                    De spreadsheet-ID staat in de URL tussen <code>/d/</code> en <code>/edit</code>. In elk spreadsheet
                    wordt het eerste zichtbare tabblad gebruikt.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="b2bSalesSheetId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Incentives, events en meetings</FormLabel>
                        <FormControl>
                          <Input placeholder="Spreadsheet-ID" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="luxurySalesSheetId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Luxury travel</FormLabel>
                        <FormControl>
                          <Input placeholder="Spreadsheet-ID" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </section>
          )}

          {section === 'system' && (
            <section className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>SMTP</CardTitle>
                  <CardDescription>
                    Deze verbinding wordt gebruikt om website- en toekomstige systeemberichten te versturen. Het
                    wachtwoord wordt versleuteld opgeslagen en nooit opnieuw weergegeven.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="smtpHost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SMTP-host</FormLabel>
                        <FormControl>
                          <Input placeholder="smtp.example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="smtpPort"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Poort</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            max="65535"
                            {...field}
                            value={field.value ?? 587}
                            onChange={event => field.onChange(Number(event.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="smtpUsername"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gebruikersnaam</FormLabel>
                        <FormControl>
                          <Input autoComplete="username" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="smtpPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Wachtwoord</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="new-password"
                            placeholder={settings?.smtpPasswordConfigured ? 'Opgeslagen wachtwoord behouden' : ''}
                            {...field}
                            value={field.value ?? ''}
                            onChange={event => {
                              form.setValue('clearSmtpPassword', false);
                              field.onChange(event.target.value);
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          {settings?.smtpPasswordConfigured
                            ? 'Leeg laten om het opgeslagen wachtwoord te behouden.'
                            : 'Vereist wanneer de SMTP-server authenticatie gebruikt.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="smtpFrom"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Afzender e-mailadres</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="info@fame-travel.be" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="smtpSecure"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-3 space-y-0 rounded-md border p-3">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={event => field.onChange(event.target.checked)}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Gebruik directe TLS</FormLabel>
                          <FormDescription>Meestal alleen nodig voor poort 465.</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  {settings?.smtpPasswordConfigured && (
                    <div className="sm:col-span-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          form.setValue('smtpPassword', '');
                          form.setValue('clearSmtpPassword', true);
                        }}
                      >
                        Verwijder opgeslagen SMTP-wachtwoord
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Vertalingen</CardTitle>
                  <CardDescription>
                    Kies de vertaaldienst en bewaar de API-sleutel versleuteld in de backoffice.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="translatorProvider"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Vertaaldienst</FormLabel>
                        <FormControl>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            {...field}
                          >
                            <option value="azure">Azure Translator</option>
                            <option value="google">Google Cloud Translation</option>
                          </select>
                        </FormControl>
                        <FormDescription>De gekozen dienst wordt gebruikt voor cases en blogs.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {translatorProvider === 'azure' && (
                    <>
                      <FormField
                        control={form.control}
                        name="azureTranslatorRegion"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Azure-regio</FormLabel>
                            <FormControl>
                              <Input placeholder="westeurope" {...field} />
                            </FormControl>
                            <FormDescription>De regio van de Azure Translator-resource.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="azureTranslatorEndpoint"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Azure-endpoint</FormLabel>
                            <FormControl>
                              <Input placeholder="https://api.cognitive.microsofttranslator.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="azureTranslatorKey"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2">
                            <FormLabel>Azure API-sleutel</FormLabel>
                            <FormControl>
                              <Input
                                type="password"
                                autoComplete="new-password"
                                placeholder={
                                  settings?.azureTranslatorKeyConfigured ? 'Opgeslagen sleutel behouden' : ''
                                }
                                {...field}
                                value={field.value ?? ''}
                                onChange={event => {
                                  form.setValue('clearAzureTranslatorKey', false);
                                  field.onChange(event.target.value);
                                }}
                              />
                            </FormControl>
                            <FormDescription>
                              {settings?.azureTranslatorKeyConfigured
                                ? 'Leeg laten om de opgeslagen sleutel te behouden.'
                                : 'Vereist met regio voor Azure Translator.'}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {settings?.azureTranslatorKeyConfigured && (
                        <div className="sm:col-span-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              form.setValue('azureTranslatorKey', '');
                              form.setValue('clearAzureTranslatorKey', true);
                            }}
                          >
                            Verwijder opgeslagen Azure-sleutel
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  {translatorProvider === 'google' && (
                    <>
                      <FormField
                        control={form.control}
                        name="googleTranslatorKey"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2">
                            <FormLabel>Google Cloud Translation API-sleutel</FormLabel>
                            <FormControl>
                              <Input
                                type="password"
                                autoComplete="new-password"
                                placeholder={
                                  settings?.googleTranslatorKeyConfigured ? 'Opgeslagen sleutel behouden' : ''
                                }
                                {...field}
                                value={field.value ?? ''}
                                onChange={event => {
                                  form.setValue('clearGoogleTranslatorKey', false);
                                  field.onChange(event.target.value);
                                }}
                              />
                            </FormControl>
                            <FormDescription>
                              {settings?.googleTranslatorKeyConfigured
                                ? 'Leeg laten om de opgeslagen sleutel te behouden.'
                                : 'Gebruik een API-sleutel met toegang tot Cloud Translation API.'}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {settings?.googleTranslatorKeyConfigured && (
                        <div className="sm:col-span-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              form.setValue('googleTranslatorKey', '');
                              form.setValue('clearGoogleTranslatorKey', true);
                            }}
                          >
                            Verwijder opgeslagen Google-sleutel
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </section>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={updateSettings.isPending}>
              {updateSettings.isPending ? 'Bezig met opslaan...' : 'Instellingen opslaan'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
